import { type SVGAttributes } from 'react';

import { twJoin, twMerge } from 'tailwind-merge';

import { type CfAsset } from '../../types';
import { getIconType, IconType } from './utils';

export interface IconProps extends SVGAttributes<SVGElement> {
  assetFile: CfAsset;
  className?: string;
  containerClassName?: string;
  disableDefaultStyle?: boolean;
}

const getContainerClassNames = ({ iconType }: { iconType: IconType }) => {
  if (iconType === IconType.Tile) {
    return 'bg-surface-l2 group-hover:bg-surface-l2-active rounded-md p-3 w-fit';
  }

  if (iconType === IconType.App) {
    return 'rounded-md w-full max-w-icon-lg aspect-square bg-icon-app flex items-center justify-center';
  }
};

const getIconClassNames = ({ iconType }: { iconType: IconType }) => {
  let className = '[&>svg]:object-cover [&>svg]:overflow-hidden [&>svg]:box-content';

  const iconTypes = [
    IconType.Tile,
    IconType.App,
    IconType.Badge,
  ];

  if (iconTypes.includes(iconType)) {
    className = twJoin(className, '[&>svg]:fill-text-primary [&>svg]:aspect-square');
  }

  if (iconType === IconType.Tile) {
    return twJoin(className, 'lg:[&>svg]:h-6 lg:[&>svg]:w-6 [&>svg]:h-5 [&>svg]:w-5');
  }

  if (iconType === IconType.Logo) {
    return twJoin(className, '[&>svg]:w-full [&>svg]:h-16 w-full h-16');
  }

  if (iconType === IconType.Flag) {
    return twJoin(className, '[&>svg]:w-6 [&>svg]:h-6');
  }

  return twJoin(className, 'lg:[&>svg]:h-12 lg:[&>svg]:w-12 [&>svg]:h-10 [&>svg]:w-10');
};

const IconRenderer = ({
  assetFile,
  className,
  disableDefaultStyle,
  iconType,
}: { iconType: IconType } & Pick<IconProps, 'assetFile' | 'className' | 'disableDefaultStyle'>) => {
  const classNames = twMerge(
    !disableDefaultStyle && getIconClassNames({ iconType }),
    className
  );

  if (!assetFile.svg) {
    const { fields: { file: { details: { image }, url }, title } } = assetFile;

    return (
      <img
        alt={title}
        className={classNames}
        src={url}
        title={title}
        {...image}
      />
    );
  }

  return (
    <span
      dangerouslySetInnerHTML={{ __html: assetFile.svg }}
      className={classNames}
    />
  );
};

const Icon = ({
  assetFile,
  className,
  containerClassName,
  disableDefaultStyle = false,
}: IconProps) => {
  const iconType = getIconType(assetFile);

  if (!iconType) {
    // Unsupported icon type
    return null;
  }

  return (
    <div className={twMerge(!disableDefaultStyle && getContainerClassNames({ iconType }), containerClassName)}>
      <IconRenderer
        assetFile={assetFile}
        className={className}
        disableDefaultStyle={disableDefaultStyle}
        iconType={iconType}
      />
    </div>
  );
};

export default Icon;
